/**
 * Data Provider Factory
 * 
 * Exports the appropriate data provider based on the current mode setting.
 * The mode is stored in localStorage and can be switched by the user.
 */

import { SupabaseProvider } from './supabaseProvider'
import { StandaloneProvider } from './standaloneProvider'
import type { DataProvider } from './types'
import { logger } from '@/lib/logger'

// Re-export types for convenience
export * from './types'
export * from './helpers'
export * from './migrationService'
export { SupabaseProvider } from './supabaseProvider'
export { StandaloneProvider } from './standaloneProvider'

// Storage key for mode setting
const MODE_STORAGE_KEY = 'ambi-app-mode'

// Valid modes
export type AppMode = 'online' | 'standalone'

// Singleton provider instance
let providerInstance: DataProvider | null = null
let currentMode: AppMode | null = null

/**
 * Get the current app mode from localStorage
 */
export function getAppMode(): AppMode {
  const stored = localStorage.getItem(MODE_STORAGE_KEY)
  if (stored === 'standalone') {
    return 'standalone'
  }
  return 'online' // Default to online
}

/**
 * Set the app mode (requires page reload to take effect)
 */
export function setAppMode(mode: AppMode): void {
  localStorage.setItem(MODE_STORAGE_KEY, mode)
  logger.info(`[DataProvider] App mode set to: ${mode}`)
}

/**
 * Check if currently in standalone mode
 */
export function isStandaloneMode(): boolean {
  return getAppMode() === 'standalone'
}

/**
 * Check if currently in online mode
 */
export function isOnlineMode(): boolean {
  return getAppMode() === 'online'
}

/**
 * Get the data provider instance
 * Creates the provider on first call based on current mode
 */
export function getDataProvider(): DataProvider {
  const mode = getAppMode()
  
  // If we have an instance for the current mode, return it
  if (providerInstance && currentMode === mode) {
    return providerInstance
  }
  
  // If mode changed, cleanup old provider
  if (providerInstance && currentMode !== mode) {
    logger.warn('[DataProvider] Mode changed, provider needs reinitialization')
    // Note: Full cleanup should happen on app reload
  }
  
  // Create new provider based on mode
  if (mode === 'standalone') {
    logger.info('[DataProvider] Creating StandaloneProvider')
    providerInstance = new StandaloneProvider()
  } else {
    logger.info('[DataProvider] Creating SupabaseProvider')
    providerInstance = new SupabaseProvider()
  }
  
  currentMode = mode
  return providerInstance
}

/**
 * Initialize the data provider
 * Should be called once on app startup
 */
export async function initializeDataProvider(): Promise<DataProvider> {
  const provider = getDataProvider()
  
  if (!provider.isReady()) {
    await provider.initialize()
  }
  
  return provider
}

/**
 * Cleanup the data provider
 * Should be called on app shutdown
 */
export async function cleanupDataProvider(): Promise<void> {
  if (providerInstance) {
    await providerInstance.cleanup()
    providerInstance = null
    currentMode = null
  }
}

/**
 * Switch to a different mode
 * This sets the mode and triggers a page reload for clean initialization
 */
export async function switchMode(newMode: AppMode): Promise<void> {
  const currentModeValue = getAppMode()
  
  if (currentModeValue === newMode) {
    logger.info(`[DataProvider] Already in ${newMode} mode`)
    return
  }
  
  logger.info(`[DataProvider] Switching from ${currentModeValue} to ${newMode} mode`)
  
  // Cleanup current provider
  await cleanupDataProvider()
  
  // Set new mode
  setAppMode(newMode)
  
  // Reload page for clean state
  window.location.reload()
}

/**
 * Export default provider for direct import
 * Usage: import { dataProvider } from '@/lib/dataProvider'
 */
export const dataProvider = getDataProvider()

// Default export
export default dataProvider
